import React, { useEffect, useMemo, useState } from 'react';

const MaterialSelectorModal = ({
  availableMaterials = [],
  selectedMaterials = [],
  onConfirm,
  onCancel,
  allowMultiple = true,
  title = 'Select Materials',
  groupBy = 'type', // 'type' or 'category'
  jobType = null,
}) => {
  const [localSelection, setLocalSelection] = useState(selectedMaterials);
  const [vinylCollapsed, setVinylCollapsed] = useState(jobType === 'mounted-boards');

  useEffect(() => {
    setLocalSelection(selectedMaterials);
  }, [selectedMaterials]);

  const groupedMaterials = useMemo(() => {
    if (groupBy === 'category') {
      // For category grouping, group only by category
      return availableMaterials.reduce((acc, material) => {
        const groupKey = material.category || 'Other';
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(material);
        return acc;
      }, {});
    } else {
      // For type grouping, group by type first, then by category within each type
      return availableMaterials.reduce((acc, material) => {
        const typeKey = material.type || 'other';
        if (!acc[typeKey]) {
          acc[typeKey] = {};
        }

        const categoryKey = material.category || 'Other';
        if (!acc[typeKey][categoryKey]) {
          acc[typeKey][categoryKey] = [];
        }
        acc[typeKey][categoryKey].push(material);
        return acc;
      }, {});
    }
  }, [availableMaterials, groupBy]);

  const suggestedVinylId = 'gloss-outdoor-vinyl-premium';

  const shouldForceSingleSelectionForType = (materialType) => {
    if (jobType === 'mounted-boards' && materialType === 'board') {
      return true;
    }
    if (jobType === 'posters' && materialType === 'poster') {
      return true;
    }
    return false;
  };

  const isSuggestedMaterial = (material) =>
    jobType === 'mounted-boards' && material.type === 'vinyl' && material.id === suggestedVinylId;

  useEffect(() => {
    setVinylCollapsed(jobType === 'mounted-boards');
  }, [jobType]);

  useEffect(() => {
    let nextSelection = Array.isArray(selectedMaterials) ? [...selectedMaterials] : [];

    if (jobType === 'mounted-boards') {
      const suggestedVinyl = availableMaterials.find(
        (material) => material.type === 'vinyl' && material.id === suggestedVinylId,
      );
      const hasSuggestedVinyl = nextSelection.some(
        (material) => material.type === 'vinyl' && material.id === suggestedVinylId,
      );

      if (suggestedVinyl && !hasSuggestedVinyl) {
        nextSelection = [
          ...nextSelection,
          {
            id: suggestedVinyl.id,
            type: suggestedVinyl.type,
            name: suggestedVinyl.name,
            width: suggestedVinyl.width,
            height: suggestedVinyl.height,
          },
        ];
      }
    }

    setLocalSelection(nextSelection);
  }, [availableMaterials, jobType, selectedMaterials]);

  const handleToggleMaterial = (material) => {
    const exists = localSelection.some(
      (selected) => selected.id === material.id && selected.type === material.type,
    );

    if (allowMultiple) {
      if (exists) {
        setLocalSelection(
          localSelection.filter(
            (selected) => !(selected.id === material.id && selected.type === material.type),
          ),
        );
      } else {
        const enforceSingleForType = shouldForceSingleSelectionForType(material.type);
        const nextSelection = enforceSingleForType
          ? localSelection.filter((selected) => selected.type !== material.type)
          : localSelection;

        setLocalSelection([
          ...nextSelection,
          {
            id: material.id,
            type: material.type,
            name: material.name,
            width: material.width,
            height: material.height,
          },
        ]);
      }
    } else {
      if (exists) {
        setLocalSelection([]);
      } else {
        setLocalSelection([
          {
            id: material.id,
            type: material.type,
            name: material.name,
            width: material.width,
            height: material.height,
          },
        ]);
      }
    }
  };

  const handleConfirm = () => {
    onConfirm(localSelection);
  };

  const handleCancel = () => {
    onCancel();
    setLocalSelection(selectedMaterials);
  };

  const getGroupLabel = (groupKey) => {
    // Format the group label nicely
    return groupKey.replace(/[-_]/g, ' ');
  };

  const getMaterialDisplayName = (selection) => {
    const match = availableMaterials.find(
      (material) => material.id === selection.id && material.type === selection.type,
    );
    return match?.name || selection.name || selection.id;
  };

  const renderMaterials = (materials) => {
    return materials.map((material) => {
      const isSelected = localSelection.some(
        (selected) => selected.id === material.id && selected.type === material.type,
      );
      const suggested = isSuggestedMaterial(material);

      let buttonClasses = 'rounded border px-3 py-2 text-left transition-colors';
      if (isSelected) {
        buttonClasses += ' border-blue-500 bg-blue-50';
      } else {
        buttonClasses += ' border-gray-200 hover:bg-gray-50';
      }

      return (
        <button
          key={`${material.type}-${material.id}`}
          type="button"
          onClick={() => handleToggleMaterial(material)}
          className={buttonClasses}
        >
          <div className="flex items-center justify-between">
            <span className="font-medium">{material.name}</span>
            {material.color_hex && (
              <span
                className="ml-2 h-4 w-4 rounded border border-gray-300"
                style={{ backgroundColor: material.color_hex }}
                title={material.color_hex}
              />
            )}
          </div>
          {suggested && (
            <div className="mt-1 text-xs font-medium text-amber-700">
              Suggested for mounted boards
            </div>
          )}
          {material.detail && (
            <div className="mt-1 text-xs text-gray-500">{material.detail}</div>
          )}
        </button>
      );
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="flex max-h-[80vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-lg">
        <div className="flex items-center justify-between border-b border-gray-200 p-6 pb-4">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button
            type="button"
            onClick={handleCancel}
            className="text-gray-500 transition-colors hover:text-gray-700"
            aria-label="Close material selector"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {Object.keys(groupedMaterials).length === 0 ? (
            <p className="text-sm text-gray-600">No materials available for this job type.</p>
          ) : (
            <div className="space-y-4">
              {groupBy === 'category' ? (
                // Flat grouping by category only (for cut vinyl)
                Object.entries(groupedMaterials).map(([categoryKey, materials]) => (
                  <div key={categoryKey} className="rounded border border-gray-200">
                    <div className="bg-gray-100 px-4 py-2 text-sm font-medium">
                      {categoryKey}
                    </div>
                    <div className="grid gap-2 p-4 md:grid-cols-2">
                      {renderMaterials(materials)}
                    </div>
                  </div>
                ))
              ) : (
                // Nested grouping by type, then category (for other job types)
                Object.entries(groupedMaterials)
                  .sort(([typeA], [typeB]) => {
                    // Start with specific overrides for mounted-boards
                    if (jobType === 'mounted-boards') {
                      if (typeA === 'vinyl' && typeB !== 'vinyl') return -1;
                      if (typeB === 'vinyl' && typeA !== 'vinyl') return 1;
                    }
                    if (typeA === 'vinyl' && typeB === 'board') return -1; // General preference
                    if (typeA === 'board' && typeB === 'vinyl') return 1;
                    return typeA.localeCompare(typeB);
                  })
                  .map(([typeKey, categoriesObj]) => {
                    const isVinylGroup = jobType === 'mounted-boards' && typeKey === 'vinyl';
                    const selectedVinylNames = localSelection
                      .filter((selected) => selected.type === 'vinyl')
                      .map(getMaterialDisplayName)
                      .filter(Boolean);
                    const vinylHeading = selectedVinylNames.length
                      ? `Vinyl - ${selectedVinylNames.join(', ')} Selected`
                      : 'Vinyl';

                    return (
                      <div key={typeKey} className="rounded border border-gray-200">
                        <div className="bg-gray-100 px-4 py-2 text-sm font-medium capitalize flex items-center justify-between">
                          <span>{isVinylGroup ? vinylHeading : getGroupLabel(typeKey)}</span>
                          {isVinylGroup && (
                            <button
                              type="button"
                              className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                              onClick={() => setVinylCollapsed((prev) => !prev)}
                            >
                              {vinylCollapsed ? 'Show' : 'Hide'}
                            </button>
                          )}
                        </div>
                        {(!isVinylGroup || !vinylCollapsed) && (
                          <div className="p-4 space-y-4">
                            {Object.entries(categoriesObj).map(([categoryKey, materials]) => (
                              <div key={`${typeKey}-${categoryKey}`}>
                                <h4 className="text-sm font-semibold text-gray-700 mb-2 px-1">
                                  {categoryKey}
                                </h4>
                                <div className="grid gap-2 md:grid-cols-2">
                                  {renderMaterials(materials)}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 p-6 pt-4">
          <button
            type="button"
            onClick={handleCancel}
            className="rounded border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-200"
            disabled={localSelection.length === 0}
          >
            Confirm Selection
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaterialSelectorModal;
